/**
 * StepMember (Plan 11, Task 3).
 *
 * Per-member form (FND-03 / FND-04 / D-02 single-step-per-member).
 *
 * Composes:
 *   - Name + short name (nickname)
 *   - Role radio (admin / teen / child)
 *   - Birth date (max = today)
 *   - ColourSlotPicker with per-family slot uniqueness
 *   - Innlogging (#141): brukernavn (valgfritt — serveren lager et fra
 *     kortnavnet), passord + bekreft (påkrevd for voksne, valgfritt ellers)
 *     og PIN for kjøkkenskjermen bak en egen knapp (valgfri)
 *   - AllergyChipPicker (merge-blocking confirmation!)
 *   - DietaryChipPicker
 *   - Notes textarea
 *
 * Submit is disabled until:
 *   - All required fields non-empty
 *   - brukernavnet er gyldig (eller tomt), passordet gyldig og likt i begge
 *     felt (eller tomt for barn/tenåringer), PIN-ene like hvis PIN er valgt
 *   - colorSlot chosen
 *   - allergies.confirmed === true  (safety gate)
 *
 * On save, posts to /api/onboarding/member; on success pushes the
 * StoredMember into Zustand state, resets the draft, and stays on
 * step 2 so the admin can add more members.  "Neste" advances to
 * step 3, enabled once the signed-in admin has saved «Om deg» (FF-11,
 * #179: the family already has its admin — the one setting it up).
 *
 * #159 steg 9f: Oppsett-skissen — «Hvem bor her?», navn og bursdag på én
 * rad, rolle som chips (ekte radioknapper i en `<label>` med `chipClass`;
 * radioen ligger usynlig over hele chipen, så den kan trykkes og fokuseres),
 * amber allergichips og bekreftelsesrad, og Tilbake / Neste i
 * `WizardFooter`.  Neste er det gamle «Ferdig med familien» (`finish-family`)
 * og krever at «Om deg» er lagret (FF-11); hvert medlem lagres med «Lagre medlem»
 * (`member-save`), som fortsatt krever allergibekreftelsen.  Så lenge et
 * medlem er påbegynt (navnet er fylt ut), er både Tilbake og Neste sperret:
 * begge ville kastet utkastet, allergiene med, uten å si fra.  Endres navnet
 * etter at allergiene er bekreftet, må de bekreftes på nytt, fordi
 * bekreftelsen nevner personen ved navn.  Feltene skissen
 * ikke viser (kallenavn, brukernavn, passord, PIN, egne allergier og
 * kosthold) er beholdt.
 *
 * #166: et medlem er påbegynt så snart ett felt er satt, ikke bare navnet,
 * også tekst i «+»-feltene for allergi og kosthold.  Påbegynt sperrer Tilbake
 * og Neste med en melding, og utkastet kan bare kastes med «Forkast utkastet»
 * og en bekreftelse.  Nettleserens Tilbake (`useBlocker`) spør på samme måte,
 * og omlasting/lukking advarer (`beforeunload`).  Kostholdstekst som ikke er
 * lagt til, sperrer «Lagre medlem» slik allergiteksten sperrer bekreftelsen,
 * og alle felt nullstilles for neste medlem.  Bursdagens `max` er Oslo-dagen.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBlocker, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { normalizeUsername, type ColorSlot } from '@stell/shared';
import { splitAuthError, type ApiErrorLike } from '../../auth/authApi';
import {
  EMPTY_NEW_PASSWORD,
  NewPasswordFields,
  OptionalPinKeypads,
  UsernameField,
  isNewPasswordValid,
  isPinPairValid,
  usernameError,
  type NewPasswordValue,
  type PinPairValue,
} from '../../auth/AuthFields';
import {
  AllergyChipPicker,
  type AllergyChipPickerValue,
} from '../components/AllergyChipPicker';
import {
  DietaryChipPicker,
  type DietaryChipPickerValue,
} from '../components/DietaryChipPicker';
import { ColourSlotPicker } from '../components/ColourSlotPicker';
import { WizardFooter } from '../components/WizardFooter';
import { Button } from '../../../components/primitives/Button';
import { chipClass } from '../../../components/primitives/Chip';
import { SectionLabel } from '../../../components/primitives/SectionLabel';
import { osloToday } from '../../../lib/osloToday';
import { useOnboardingState } from '../useOnboardingState';
import { OnboardingApi, OnboardingApiError } from '../api';

interface MemberDraft {
  name: string;
  shortName: string;
  role: 'admin' | 'teen' | 'child';
  birthDate: string;
  colorSlot: ColorSlot | null;
  username: string;
  password: NewPasswordValue;
  /** null = ingen PIN (standard); ellers PIN-paret som tastes inn. */
  pin: PinPairValue | null;
  allergies: AllergyChipPickerValue;
  dietary: DietaryChipPickerValue;
  notes: string;
  /** Tekst i «+»-feltene som ikke er lagt til ennå (#166). */
  allergyPending: string;
  dietaryPending: string;
}

const EMPTY_DRAFT: MemberDraft = {
  name: '',
  shortName: '',
  role: 'child',
  birthDate: '',
  colorSlot: null,
  username: '',
  password: EMPTY_NEW_PASSWORD,
  pin: null,
  allergies: { chips: [], custom: [], confirmed: false },
  dietary: { chips: [], custom: [] },
  notes: '',
  allergyPending: '',
  dietaryPending: '',
};

/**
 * Påbegynt = minst ett felt er endret fra det tomme utkastet (#166).  Både
 * allergier og kosthold teller, også uten navn, så de aldri kastes stille.
 */
function isDraftStarted(d: MemberDraft): boolean {
  return (
    d.name.trim() !== '' ||
    d.shortName.trim() !== '' ||
    d.role !== EMPTY_DRAFT.role ||
    d.birthDate !== '' ||
    d.colorSlot !== null ||
    d.username.trim() !== '' ||
    d.password.password !== '' ||
    d.password.confirm !== '' ||
    d.pin !== null ||
    d.allergies.chips.length > 0 ||
    d.allergies.custom.length > 0 ||
    d.allergies.confirmed ||
    d.dietary.chips.length > 0 ||
    d.dietary.custom.length > 0 ||
    d.notes.trim() !== '' ||
    d.allergyPending.trim() !== '' ||
    d.dietaryPending.trim() !== ''
  );
}

export function StepMember() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const members = useOnboardingState((s) => s.members);
  const ownColor = useOnboardingState((s) => s.ownColor);
  // FF-11 (#179): familien har alltid en admin — den som er logget inn. Det
  // som må være på plass før Neste, er at hen har lagret «Om deg».
  const profileConfirmed = useOnboardingState((s) => s.profileConfirmed);
  const addMember = useOnboardingState((s) => s.addMember);
  const setStep = useOnboardingState((s) => s.setStep);

  const [draft, setDraft] = useState<MemberDraft>(EMPTY_DRAFT);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<ApiErrorLike | null>(null);
  // Øker for hvert nytt medlem; skjemaet monteres på nytt, så heller ikke
  // lokal tilstand i feltene (vis passord, PIN-tastatur) følger med (#166).
  const [memberSeq, setMemberSeq] = useState(0);
  const [askDiscard, setAskDiscard] = useState(false);
  const ids = {
    name: useId(),
    nickname: useId(),
    birthdate: useId(),
    discardTitle: useId(),
  };

  const takenSlots = useMemo(
    () => [...members.map((m) => m.colorSlot), ...(ownColor ? [ownColor] : [])],
    [members, ownColor],
  );
  const draftName = draft.name.trim();
  const draftStarted = isDraftStarted(draft);
  const draftLabel = draftName || t('onboarding.step3.unsaved_member_fallback');
  const today = osloToday();

  // All navigasjon bort fra steget må bekreftes mens et medlem er påbegynt,
  // i praksis nettleserens Tilbake: Tilbake/Neste her er sperret da, og steget
  // har ingen andre omdirigeringer.  Også en framtidig omdirigering spør først,
  // fordi den ellers ville kastet allergiene.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      draftStarted && currentLocation.pathname !== nextLocation.pathname,
  );
  const discardOpen = askDiscard || blocker.state === 'blocked';

  // Omlasting og lukking kan ikke stoppes med en egen dialog; nettleseren
  // spør med sin egen.
  useEffect(() => {
    if (!draftStarted) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      // Eldre nettlesere krever returnValue for å vise spørsmålet.
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [draftStarted]);

  function resetDraft() {
    setDraft(EMPTY_DRAFT);
    setMemberSeq((n) => n + 1);
  }

  function keepDraft() {
    setAskDiscard(false);
    if (blocker.state === 'blocked') blocker.reset();
  }

  function discardDraft() {
    setAskDiscard(false);
    resetDraft();
    setApiError(null);
    if (blocker.state === 'blocked') blocker.proceed();
  }

  // Voksne må ha passord: de logger inn på telefonen og låser opp
  // administrasjon (#139). Barn kan få det senere via en lenke.
  const passwordRequired = draft.role === 'admin';
  const passwordGiven = draft.password.password !== '' || draft.password.confirm !== '';
  const formValid =
    draft.name.trim().length > 0 &&
    draft.birthDate !== '' &&
    // ISO-datoer sammenlignes som tekst; Oslo-dagen, ikke UTC (#166).
    draft.birthDate <= today &&
    draft.colorSlot !== null &&
    usernameError(draft.username) === null &&
    (passwordRequired || passwordGiven ? isNewPasswordValid(draft.password) : true) &&
    isPinPairValid(draft.pin) &&
    // Tekst som ikke er lagt til med «+», ville ikke blitt lagret (#166).
    draft.allergyPending.trim() === '' &&
    draft.dietaryPending.trim() === '' &&
    draft.allergies.confirmed; // the merge-blocking gate

  async function onSave() {
    if (!formValid || !draft.colorSlot || submitting) return;
    setSubmitting(true);
    setApiError(null);
    try {
      const res = await OnboardingApi.createMember({
        name: draft.name.trim(),
        shortName: draft.shortName.trim() || null,
        role: draft.role,
        birthDate: draft.birthDate,
        colorSlot: draft.colorSlot,
        ...(draft.username.trim() !== ''
          ? { username: normalizeUsername(draft.username) }
          : {}),
        ...(passwordGiven
          ? { password: draft.password.password, passwordConfirm: draft.password.confirm }
          : {}),
        ...(draft.pin !== null ? { pin: draft.pin.pin, pinConfirm: draft.pin.pinConfirm } : {}),
        allergies: {
          chips: draft.allergies.chips,
          custom: draft.allergies.custom,
          confirmed: true,
        },
        dietary: {
          chips: draft.dietary.chips,
          custom: draft.dietary.custom,
        },
        notes: draft.notes.trim() || null,
      });
      addMember({
        memberId: res.memberId,
        name: draft.name.trim(),
        role: draft.role,
        colorSlot: res.colorSlot,
      });
      resetDraft();
    } catch (err) {
      if (err instanceof OnboardingApiError) {
        setApiError({
          code: typeof err.body.error === 'string' ? err.body.error : 'unknown',
          ...(typeof err.body.reason === 'string' ? { reason: err.body.reason } : {}),
        });
      } else {
        setApiError({ code: 'network' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Feil som hører til et felt vises under feltet; resten nederst.
  const apiErrors = splitAuthError(t, apiError, t('onboarding.load_error'));

  function goFinish() {
    if (!profileConfirmed || draftStarted) return;
    setStep(3);
    navigate('/onboarding/step-3');
  }

  function goBack() {
    if (draftStarted) return;
    setStep(1);
    navigate('/onboarding/step-1');
  }

  return (
    <section data-testid="step-member" className="flex flex-1 flex-col gap-5">
      <header>
        <div className="flex items-center justify-between gap-3">
          <h1 className="m-0 text-heading-l font-extrabold tracking-tight text-ink">
            {t('onboarding.step3.title')}
          </h1>
          {members.length > 0 ? (
            <span data-testid="member-count" className="shrink-0 text-sm font-bold text-ink-2">
              {t(
                members.length === 1
                  ? 'onboarding.members_so_far'
                  : 'onboarding.members_so_far_plural',
                { count: members.length },
              )}
            </span>
          ) : null}
        </div>
        <p className="m-0 mt-1 text-body-s text-ink-2">
          {t('onboarding.step3.subtitle', { n: members.length + 1 })}
        </p>
      </header>

      <form
        key={memberSeq}
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          void onSave();
        }}
      >
        <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-2.5">
          <div>
            <SectionLabel as="label" htmlFor={ids.name}>
              {t('onboarding.step3.name')}
            </SectionLabel>
            <input
              id={ids.name}
              data-testid="member-name"
              className="field"
              type="text"
              maxLength={60}
              value={draft.name}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  name: e.target.value,
                  allergies: { ...draft.allergies, confirmed: false },
                })
              }
            />
          </div>
          <div>
            <SectionLabel as="label" htmlFor={ids.birthdate}>
              {t('onboarding.step3.birthdate')}
            </SectionLabel>
            <input
              id={ids.birthdate}
              data-testid="member-birthdate"
              className="field"
              type="date"
              max={today}
              value={draft.birthDate}
              onChange={(e) =>
                setDraft({ ...draft, birthDate: e.target.value })
              }
            />
          </div>
        </div>

        <div>
          <SectionLabel as="label" htmlFor={ids.nickname}>
            {t('onboarding.step3.nickname')}
          </SectionLabel>
          <input
            id={ids.nickname}
            data-testid="member-short-name"
            className="field"
            type="text"
            maxLength={40}
            value={draft.shortName}
            onChange={(e) =>
              setDraft({ ...draft, shortName: e.target.value })
            }
          />
        </div>

        <fieldset className="m-0 border-0 p-0" data-testid="member-role">
          <SectionLabel as="legend">{t('onboarding.step3.role')}</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {(['admin', 'teen', 'child'] as const).map((r) => (
              <label
                key={r}
                className={`${chipClass(draft.role === r)} relative cursor-pointer px-4 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-primary`}
              >
                <input
                  type="radio"
                  name="role"
                  value={r}
                  checked={draft.role === r}
                  onChange={() => setDraft({ ...draft, role: r })}
                  data-testid={`role-${r}`}
                  className="absolute inset-0 m-0 cursor-pointer appearance-none rounded-pill opacity-0"
                />
                {t(`onboarding.step3.role_${r}`)}
              </label>
            ))}
          </div>
        </fieldset>

        <ColourSlotPicker
          value={draft.colorSlot}
          takenSlots={takenSlots}
          onChange={(slot) => setDraft({ ...draft, colorSlot: slot })}
        />

        <fieldset className="m-0 flex flex-col gap-4 border-0 p-0" data-testid="member-login-fields">
          <SectionLabel as="legend">{t('auth.login_legend')}</SectionLabel>
          <UsernameField
            label={t('onboarding.step3.username')}
            value={draft.username}
            onChange={(username) => setDraft({ ...draft, username })}
            testId="member-username"
            hint={t('onboarding.step3.username_hint')}
            error={apiErrors.username}
            autoComplete="off"
          />
          <p className="m-0 text-sm text-ink-2" data-testid="member-password-hint">
            {passwordRequired
              ? t('onboarding.step3.password_admin_hint')
              : t('onboarding.step3.password_later_hint')}
          </p>
          <NewPasswordFields
            value={draft.password}
            onChange={(password) => setDraft({ ...draft, password })}
            testId="member-password"
            label={
              passwordRequired
                ? t('onboarding.step3.password_admin')
                : t('onboarding.step3.password_optional')
            }
            error={apiErrors.password}
          />
          <OptionalPinKeypads
            value={draft.pin}
            onChange={(pin) => setDraft((d) => ({ ...d, pin }))}
            addTestId="member-add-pin"
          />
        </fieldset>

        <AllergyChipPicker
          memberName={draft.name.trim() || t('onboarding.step3.name')}
          value={draft.allergies}
          onChange={(allergies) => setDraft((d) => ({ ...d, allergies }))}
          pendingText={draft.allergyPending}
          onPendingTextChange={(allergyPending) => setDraft((d) => ({ ...d, allergyPending }))}
        />

        <DietaryChipPicker
          value={draft.dietary}
          onChange={(dietary) => setDraft((d) => ({ ...d, dietary }))}
          pendingText={draft.dietaryPending}
          onPendingTextChange={(dietaryPending) => setDraft((d) => ({ ...d, dietaryPending }))}
        />

        {apiErrors.other ? (
          <p data-testid="member-api-error" role="alert" className="m-0 text-sm text-bad">
            {apiErrors.other}
          </p>
        ) : null}

        <Button
          data-testid="member-save"
          type="submit"
          variant="solid"
          size={52}
          disabled={!formValid || submitting}
        >
          {t('onboarding.step3.save_member')}
        </Button>
      </form>

      <WizardFooter
        onBack={goBack}
        backDisabled={draftStarted}
        hint={
          draftStarted ? (
            <div className="flex flex-col items-center gap-1">
              <p data-testid="finish-family-hint" className="m-0 text-center text-sm text-ink-2">
                {t('onboarding.step3.unsaved_member', { name: draftLabel })}
              </p>
              <Button
                data-testid="member-discard"
                variant="ghost"
                onClick={() => setAskDiscard(true)}
              >
                {t('onboarding.step3.discard')}
              </Button>
            </div>
          ) : !profileConfirmed ? (
            <p data-testid="finish-family-hint" className="m-0 text-center text-sm text-ink-2">
              {t('onboarding.about.need_profile')}
            </p>
          ) : null
        }
      >
        <Button
          data-testid="finish-family"
          size={56}
          className="flex-[2]"
          disabled={!profileConfirmed || draftStarted}
          onClick={goFinish}
        >
          {t('onboarding.next')}
        </Button>
      </WizardFooter>

      {/* Portal, som de andre bekreftelsene: et glasskort med backdrop-filter
          ville ellers ramme inn `fixed`-dimmingen (#159 steg 9c). */}
      {discardOpen
        ? createPortal(
            <div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby={ids.discardTitle}
              data-testid="member-discard-dialog"
              className="fixed inset-0 z-50 flex items-center justify-center bg-dim p-4"
            >
              <div className="w-full max-w-sm space-y-4 rounded-lg bg-sheet p-6 shadow-lg">
                <h2 id={ids.discardTitle} className="m-0 text-heading-s font-extrabold text-ink">
                  {t('onboarding.step3.discard_title')}
                </h2>
                <p className="m-0 text-sm text-ink-2">
                  {t('onboarding.step3.discard_body', { name: draftLabel })}
                </p>
                <div className="flex justify-end gap-3">
                  <Button data-testid="member-discard-keep" variant="ghost" onClick={keepDraft}>
                    {t('onboarding.step3.discard_keep')}
                  </Button>
                  <Button data-testid="member-discard-confirm" variant="danger" onClick={discardDraft}>
                    {t('onboarding.step3.discard_confirm')}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </section>
  );
}
